// src/pages/LandingPage.jsx
import { Helmet } from 'react-helmet-async';
import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { Link } from 'react-router-dom';
import {
  MessageSquare, CreditCard, ShoppingBag, Clock, Users, Zap,
  Play, Send, MessageCircle, Loader2, Rocket, CheckCircle, CheckCircle2,
  ArrowRight, Moon, Sun, Menu, X, Mail, Phone, MapPin,
  Facebook, Twitter, Linkedin, Instagram, Star, Quote, Sparkles, TrendingUp,
} from 'lucide-react';

// ─── API BASE URL ─────────────────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

// ─── SITE CONFIG ──────────────────────────────────────────────────────────────
const siteConfig = {
  name: 'TrueChattr',
  tagline: '10X Your Business Growth',
  headline: 'with WhatsApp Automation',
  description:
    'Automate your WhatsApp completely. Set keywords, send bulk messages to 1000+ customers, track leads — all in one dashboard. No coding required.',
  cta: { primary: 'Sign up - It’s FREE', secondary: 'See How It Works' },
};

// ─── STATS DATA ───────────────────────────────────────────────────────────────
const statsData = [
  { id: 1, value: '98%',    label: 'Open Rates',      description: 'WhatsApp messages get opened' },
  { id: 2, value: '45-60%', label: 'Click Rates',     description: 'Drive maximum engagement' },
  { id: 3, value: '2.60Bn+',label: 'Active Users',    description: 'WhatsApp users worldwide' },
  { id: 4, value: '70%',    label: 'Engagement Rate', description: 'Unmatched customer engagement' },
];

// ─── FEATURES DATA ────────────────────────────────────────────────────────────
const featuresData = [
  {
    id: 1,
    title: 'Keyword-Based Auto Reply',
    subtitle: 'Instant replies, zero manual effort',
    description:
      'Set keywords that customers send — like "price", "order", "location" — and define what your bot replies. Works 24/7 automatically.',
    features: [
      'Keyword trigger setup in minutes',
      'Multiple keywords per reply template',
      'Priority-based keyword matching',
      'Active / inactive toggle per keyword',
    ],
    image: 'https://image2url.com/r2/default/images/1772533938884-221d1513-3b13-4748-bc63-3cf8bf401b3d.png',
    imageAlt: 'WhatsApp keyword auto reply dashboard',
  },
  {
    id: 2,
    title: 'Bulk Broadcasting',
    subtitle: 'Reach thousands in one click',
    description:
      'Send promotional campaigns, offers, and updates directly to your customers on WhatsApp. One click, thousands of messages.',
    features: [
      'Send to 1000+ customers at once',
      'Rich media support — images, documents',
      'Personalized message templates with variables',
      'Officially approved by WhatsApp Business API',
    ],
    image: 'https://image2url.com/r2/default/images/1772533592851-834027d9-f8b8-4193-b868-d7dd377ee7b1.png',
    imageAlt: 'WhatsApp bulk broadcast messaging',
  },
  {
    id: 3,
    title: 'Lead Management',
    subtitle: 'Capture, track, convert',
    description:
      'Collect leads directly from WhatsApp conversations. Track status, add notes, export to CSV — everything in one place.',
    features: [
      'Lead capture from conversations',
      'Status tracking — new, contacted, converted',
      'CSV export for follow-ups',
      'Bulk message your leads directly',
    ],
    image: 'https://image2url.com/r2/default/images/1772534064885-ebc810de-4355-4fe7-9ccc-a9de183f18e6.png',
    imageAlt: 'WhatsApp lead management dashboard',
  },
  {
    id: 4,
    title: 'Analytics Dashboard',
    subtitle: 'Know what is working',
    description:
      'Track every message, monitor keyword performance, and see how your automation is saving you time every single day.',
    features: [
      'Message volume over time charts',
      'Keyword usage and performance stats',
      'Bot automation rate tracking',
      'Conversation status overview',
    ],
    image: 'https://image2url.com/r2/default/images/1772534133055-4cc1ccbe-07db-43c7-b298-cd602deb6a98.png',
    imageAlt: 'WhatsApp analytics dashboard',
  },
];

// ─── ADDITIONAL FEATURES ──────────────────────────────────────────────────────
const additionalFeatures = [
  {
    id: 1,
    icon: 'MessageSquare',
    title: 'Conversations Dashboard',
    description:
      'See all your WhatsApp chats in one place. Manage status, set priority, and keep track of every conversation.',
  },
  {
    id: 2,
    icon: 'ShoppingBag',
    title: 'Product Catalog',
    description:
      'Add your products with prices and descriptions. Customers ask — bot replies with product details automatically.',
  },
  {
    id: 3,
    icon: 'Users',
    title: 'FAQ Bot',
    description:
      'Set common questions and answers. The bot handles FAQs automatically so your team handles only what matters.',
  },
  {
    id: 4,
    icon: 'Clock',
    title: 'Business Hours',
    description:
      'Set your availability hours and auto-respond to customers outside business hours.',
  },
  {
    id: 5,
    icon: 'Zap',
    title: 'Quick Reply Templates',
    description:
      'Create reusable message templates with variables like {customer_name} for fast, personalized replies.',
  },
  {
    id: 6,
    icon: 'CreditCard',
    title: 'Message Templates',
    description:
      'Professional broadcast templates with image headers, body text, and variable replacement support.',
  },
];

// ─── FAQ DATA ─────────────────────────────────────────────────────────────────
const faqData = [
  {
    id: 1,
    question: 'What is TrueChattr?',
    answer:
      'TrueChattr is a WhatsApp automation platform that helps businesses automate replies, send bulk messages, manage leads, and track conversations — all in one dashboard. No coding required.',
  },
  {
    id: 2,
    question: 'How does the auto-reply work?',
    answer:
      'You set keywords (e.g. "price", "order", "hours") and write what the bot should reply when a customer sends that keyword. When a customer messages you with that keyword, the bot automatically sends your reply instantly.',
  },
  {
    id: 3,
    question: 'Is TrueChattr officially approved by WhatsApp?',
    answer:
      'Yes! TrueChattr uses Official WhatsApp Business APIs, ensuring full compliance with WhatsApp policies. Your business gets access to advanced features like bulk messaging and approved message templates.',
  },
  {
    id: 4,
    question: "What's the difference between WhatsApp Business App and TrueChattr?",
    answer:
      'WhatsApp Business App is limited to one device and requires manual responses. TrueChattr gives you keyword automation, bulk broadcasting to thousands, lead management, analytics, and a full dashboard — perfect for businesses handling high message volumes.',
  },
  {
    id: 5,
    question: 'Can I send bulk promotional messages?',
    answer:
      "Absolutely! You can send campaigns to thousands of customers at once — offers, updates, announcements. All messages go through WhatsApp's approved template system to ensure compliance.",
  },
  {
    id: 6,
    question: 'How quickly can I get started?',
    answer:
      "You can register and start immediately! Connect your WhatsApp Business account, set up your first keyword, and your bot is live. Takes about 5 minutes with no technical skills needed.",
  },
  {
    id: 7,
    question: 'What is the Basic plan?',
    answer:
      'The Basic plan is our entry-level paid plan at $19/month. It includes 1,000 messages, analytics dashboard, 25 keywords, and 50 products — perfect for small businesses ready to automate their WhatsApp.',
  },
];

// ─── BUSINESS TYPES ───────────────────────────────────────────────────────────
const businessTypes = [
  'E-commerce', 'Education', 'Healthcare', 'Real Estate',
  'Restaurant/Food', 'Retail', 'Services', 'Technology', 'SaaS', 'Agency', 'Other',
];

// ─── PRICING DATA ─────────────────────────────────────────────────────────────
const pricingData = [
  {
    id: 1,
    name: 'Starter',
    price: 'Free',
    period: 'forever',
    description: 'Perfect for testing the platform',
    features: [
      '200 messages/month',
      '1 WhatsApp number',
      'Keyword auto-reply bot',
      'Unlimited keywords',
      'Unlimited products & FAQs',
      'Analytics dashboard',
      'Lead management',
      'Bulk broadcasting',
      'Email support',
    ],
    cta: 'Start Free',
    popular: false,
    badge: null,
    monthlyPrice: null,
    yearlyPrice: null,
  },
  {
    id: 5,
    name: 'Basic',
    price: '$19',
    period: '/month',
    description: 'For small businesses getting started',
    features: [
      '2,500 messages/month',
      '1 WhatsApp number',
      'Keyword auto-reply bot',
      'Unlimited keywords',
      'Unlimited products & FAQs',
      'Analytics dashboard',
      'Lead management',
      'Bulk broadcasting',
      'Email support',
    ],
    cta: 'Get Started',
    popular: false,
    badge: null,
    monthlyPrice: 19,
    yearlyPrice: 12,
  },
  {
    id: 2,
    name: 'Pro',
    price: '$49',
    period: '/month',
    description: 'Best for growing businesses',
    features: [
      '7,500 messages/month',
      '1 WhatsApp number',
      'Keyword auto-reply bot',
      'Unlimited keywords',
      'Unlimited products & FAQs',
      'Analytics dashboard',
      'Lead management',
      'Bulk broadcasting',
      'Priority support',
    ],
    cta: 'Get Started',
    popular: true,
    badge: 'Most Popular',
    monthlyPrice: 49,
    yearlyPrice: 29,
  },
  {
    id: 3,
    name: 'Business',
    price: '$149',
    period: '/month',
    description: 'For established companies',
    features: [
      '25,000 messages/month',
      '3 WhatsApp numbers',
      'Keyword auto-reply bot',
      'Unlimited keywords',
      'Custom workflows',
      'Advanced analytics',
      'Multi-agent support',
      'API access',
      'Dedicated support',
    ],
    cta: 'Get Started',
    popular: false,
    badge: null,
    monthlyPrice: 149,
    yearlyPrice: 99,
  },
  {
    id: 4,
    name: 'Enterprise',
    price: 'Custom',
    period: 'pricing',
    description: 'Tailored for large organizations',
    features: [
      'Unlimited messages',
      'Unlimited WhatsApp numbers',
      'Keyword auto-reply bot',
      'Multi-agent support',
      'White label options',
      'Custom integrations',
      'Dedicated account manager',
      'SLA guarantee',
      '24/7 premium support',
    ],
    cta: 'Contact Sales',
    popular: false,
    badge: null,
    monthlyPrice: null,
    yearlyPrice: null,
  },
];

// ─── LIFETIME DEAL ────────────────────────────────────────────────────────────
const lifetimeDeal = {
  title: 'Lifetime Deal',
  subtitle: 'Limited to 50 spots only! 🔥',
  price: '$499',
  originalPrice: '$1,176',
  savings: 'Save $677',
  description: 'Pay once, get Pro Plan features forever',
  features: [
    'All Pro Plan features',
    'Lifetime access — no monthly fees',
    'All future updates included',
    'Priority onboarding support',
  ],
  spotsLeft: 50,
};

// ─── PROBLEMS DATA ────────────────────────────────────────────────────────────
const problemsData = [
  {
    id: 1,
    icon: '😰',
    title: 'Manual replies taking hours daily',
    description: 'Spending precious time responding to the same questions over and over',
  },
  {
    id: 2,
    icon: '📱',
    title: 'Missing messages = lost sales',
    description: 'Customers expect instant replies. Delays mean they go to competitors',
  },
  {
    id: 3,
    icon: '👥',
    title: "Can't handle high message volume",
    description: 'As your business grows, managing hundreds of WhatsApp messages becomes impossible',
  },
];

// ─── SOLUTION STEPS ───────────────────────────────────────────────────────────
const solutionSteps = [
  {
    id: 1,
    icon: '🔗',
    title: 'Connect WhatsApp',
    description: 'Link your WhatsApp Business account in just 2 minutes. No technical skills needed.',
    time: '2 min',
  },
  {
    id: 2,
    icon: '⌨️',
    title: 'Set Your Keywords',
    description: 'Add keywords customers send and write what the bot should reply. Simple setup in minutes.',
    time: '5 min',
  },
  {
    id: 3,
    icon: '✨',
    title: 'Bot Handles Replies 24/7',
    description: 'Sit back and relax. Your bot automatically replies to customers round the clock.',
    time: '24/7',
  },
];

// ─── TESTIMONIALS — Real early access users, Hindi/relatable ─────────────────
const testimonialsData = [
  {
    id: 1,
    name: 'Rahul Sharma',
    role: 'E-commerce Seller',
    company: 'Early Access User',
    avatar: 'https://i.pravatar.cc/150?img=5',
    content:
      'WhatsApp pe manually reply karna bahut time leta tha. Ab keywords set kar diye — bot automatically sab handle kar leta hai. Bahut time bachta hai!',
    rating: 5,
    metric: 'Early Access User',
  },
  {
    id: 2,
    name: 'Priya Singh',
    role: 'Online Store Owner',
    company: 'Beta User',
    avatar: 'https://i.pravatar.cc/150?img=12',
    content:
      'Bulk messaging feature se Holi offers ek click mein hazaron customers ko bhej diye. Pehle yeh kaam 2 ghante leta tha, ab 2 minute mein ho jaata hai!',
    rating: 5,
    metric: 'Beta User',
  },
  {
    id: 3,
    name: 'Amit Patel',
    role: 'Coaching Institute Owner',
    company: 'Early Access User',
    avatar: 'https://i.pravatar.cc/150?img=8',
    content:
      'Students ke queries automatically handle ho rahe hain — fees, schedule, admission — sab. Hamara team ab sirf important calls pe focus karta hai.',
    rating: 5,
    metric: 'Early Access User',
  },
];

// ─── THEME CONTEXT ────────────────────────────────────────────────────────────
const LandingThemeCtx = createContext({ theme: 'light', toggle: () => {} });

function LandingThemeProvider({ children }) {
  const [theme, setTheme] = useState(
    () => localStorage.getItem('TrueChattr-lp-theme') || 'light'
  );

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('TrueChattr-lp-theme', theme);
  }, [theme]);

  const toggle = () => setTheme((t) => (t === 'light' ? 'dark' : 'light'));

  return (
    <LandingThemeCtx.Provider value={{ theme, toggle }}>
      {children}
    </LandingThemeCtx.Provider>
  );
}

const useLandingTheme = () => useContext(LandingThemeCtx);

// ─── ICON MAP ─────────────────────────────────────────────────────────────────
const iconMap = { MessageSquare, CreditCard, ShoppingBag, Clock, Users, Zap };

// ─── REUSABLE FIELD ───────────────────────────────────────────────────────────
const Field = ({ label, id, ...props }) => (
  <div className="space-y-2">
    {label && (
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>
    )}
    <input
      id={id}
      {...props}
      className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
    />
  </div>
);

const SelectField = ({ label, id, options, ...props }) => (
  <div className="space-y-2">
    {label && (
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>
    )}
    <select
      id={id}
      {...props}
      className="w-full h-12 px-4 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
    >
      <option value="">Select an option</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  </div>
);

const Btn = ({ children, variant = 'primary', className = '', ...props }) => {
  const base =
    'inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed';
  const styles = {
    primary:
      'bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/25 hover:shadow-emerald-600/40',
    outline:
      'border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-emerald-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20',
    ghost: 'bg-white text-emerald-600 hover:bg-gray-100',
  };
  return (
    <button className={`${base} ${styles[variant]} ${className}`} {...props}>
      {children}
    </button>
  );
};

// ─── LOGO ─────────────────────────────────────────────────────────────────────
const Logo = ({ className = '' }) => (
  <div className={`flex items-center gap-2 ${className}`}>
    <div className="relative">
      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg">
        <span className="text-2xl font-bold text-white">C</span>
      </div>
      <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white dark:border-gray-900" />
    </div>
    <span className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-emerald-500 bg-clip-text text-transparent">
      TrueChattr
    </span>
  </div>
);

// ─── HEADER ───────────────────────────────────────────────────────────────────
const Header = () => {
  const { theme, toggle } = useLandingTheme();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { label: 'Features',     href: '#features'    },
    { label: 'How It Works', href: '#how-it-works' },
    { label: 'Pricing',      href: '#pricing'      },
    { label: 'FAQ',          href: '#faq'          },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Logo />

          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((l) => (
              <a
                key={l.label}
                href={l.href}
                className="text-sm font-semibold text-gray-700 hover:text-emerald-600 dark:text-gray-300 dark:hover:text-emerald-400 transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={toggle}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle theme"
            >
              {theme === 'light' ? (
                <Moon className="w-5 h-5 text-gray-700" />
              ) : (
                <Sun className="w-5 h-5 text-gray-300" />
              )}
            </button>

            <Link
              to="/login"
              className="hidden md:inline-flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors px-3 py-2"
            >
              Log in
            </Link>

            <Link
              to="/register"
              className="hidden md:inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm rounded-xl font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/25 transition-all"
            >
              Sign up - It’s FREE
            </Link>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white dark:bg-gray-900 border-t dark:border-gray-800 shadow-lg">
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((l) => (
              <a
                key={l.label}
                href={l.href}
                onClick={() => setMobileOpen(false)}
                className="block text-base font-medium text-gray-700 hover:text-emerald-600 dark:text-gray-300 dark:hover:text-emerald-400 transition-colors py-2"
              >
                {l.label}
              </a>
            ))}
            <Link
              to="/login"
              className="block text-base font-medium text-gray-700 dark:text-gray-300 py-2"
              onClick={() => setMobileOpen(false)}
            >
              Log in
            </Link>
            <Link
              to="/register"
              onClick={() => setMobileOpen(false)}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg transition-all"
            >
              Get Early Access — Free
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

// ─── HERO SECTION ─────────────────────────────────────────────────────────────
const HeroSection = () => {
  const benefits = ['No credit card required', '100 messages free to start', 'Setup in 5 minutes'];

  return (
    <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-200/30 dark:bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-200/30 dark:bg-blue-500/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                🎉 Now in Early Access — Limited Spots
              </span>
            </div>

            <div className="space-y-4">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight tracking-tight">
                <span className="text-gray-900 dark:text-white">{siteConfig.tagline}</span>
                <br />
                <span className="bg-gradient-to-r from-emerald-600 to-emerald-500 bg-clip-text text-transparent">
                  {siteConfig.headline}
                </span>
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-xl">
                {siteConfig.description}
              </p>
            </div>

            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {benefits.map((b, i) => (
                <div key={i} className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{b}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 text-lg rounded-xl font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/25 transition-all"
              >
                {siteConfig.cta.primary}
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a href="#how-it-works">
                <Btn variant="outline" className="px-8 py-4 text-lg group w-full sm:w-auto">
                  <Play className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  {siteConfig.cta.secondary}
                </Btn>
              </a>
            </div>

            <div className="pt-2">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                Join businesses already automating with TrueChattr
              </p>
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                  </svg>
                ))}
                <span className="ml-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Early Access — Be Among The First 🚀
                </span>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://image2url.com/r2/default/images/1772533592851-834027d9-f8b8-4193-b868-d7dd377ee7b1.png?w=800&h=900&fit=crop"
                alt="TrueChattr WhatsApp Automation Dashboard"
                className="w-full h-auto object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-600/20 to-transparent" />
            </div>

            <div className="absolute -bottom-6 -left-6 bg-white dark:bg-gray-800 rounded-xl shadow-xl p-4 border border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-2xl">
                  🚀
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">98%</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Open Rate</p>
                </div>
              </div>
            </div>

            <div className="absolute -top-6 -right-6 bg-white dark:bg-gray-800 rounded-xl shadow-xl p-4 border border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-2xl">
                  ⚡
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">24/7</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Auto Reply</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── STATS SECTION ────────────────────────────────────────────────────────────
const StatsSection = () => (
  <section className="py-16 bg-gray-50 dark:bg-gray-800/50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
          Why WhatsApp?
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
          WhatsApp is the most powerful channel for business communication — higher reach, higher
          engagement than any other platform.
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
        {statsData.map((stat) => (
          <div
            key={stat.id}
            className="group bg-white dark:bg-gray-800 rounded-2xl p-6 md:p-8 text-center border border-gray-200 dark:border-gray-700 hover:border-emerald-500 dark:hover:border-emerald-500 hover:shadow-xl transition-all duration-300"
          >
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors mb-2">
              {stat.value}
            </h3>
            <p className="text-base md:text-lg font-semibold text-gray-700 dark:text-gray-300 mb-1">
              {stat.label}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">{stat.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── PROBLEM SECTION ──────────────────────────────────────────────────────────
const ProblemSection = () => (
  <section className="py-20 bg-white dark:bg-gray-900">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
          Tired of Missing{' '}
          <span className="text-emerald-600 dark:text-emerald-400">Customer Messages?</span>
        </h2>
        <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
          Every business faces these challenges. Sound familiar?
        </p>
      </div>
      <div className="grid md:grid-cols-3 gap-8">
        {problemsData.map((p) => (
          <div
            key={p.id}
            className="group relative bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 border-2 border-gray-200 dark:border-gray-700 hover:border-red-400 dark:hover:border-red-500 transition-all duration-300 hover:shadow-xl"
          >
            <div className="text-6xl mb-4">{p.icon}</div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{p.title}</h3>
            <p className="text-gray-600 dark:text-gray-400">{p.description}</p>
            <div className="absolute top-4 right-4 w-2 h-2 bg-red-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── SOLUTION SECTION ─────────────────────────────────────────────────────────
const SolutionSection = () => (
  <section
    id="how-it-works"
    className="py-20 bg-gradient-to-b from-emerald-50 to-white dark:from-gray-800 dark:to-gray-900"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
          TrueChattr Handles It All.{' '}
          <span className="text-emerald-600 dark:text-emerald-400">Automatically.</span>
        </h2>
        <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
          Get started in 3 simple steps. No coding required.
        </p>
      </div>

      <div className="relative max-w-5xl mx-auto">
        <div className="hidden md:block absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-emerald-200 via-emerald-400 to-emerald-600 dark:from-emerald-800 dark:via-emerald-600 dark:to-emerald-400 rounded-full" />
        <div className="grid md:grid-cols-3 gap-8 relative">
          {solutionSteps.map((step, i) => (
            <div
              key={step.id}
              className="relative bg-white dark:bg-gray-800 rounded-2xl p-8 border-2 border-gray-200 dark:border-gray-700 hover:border-emerald-500 dark:hover:border-emerald-400 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2"
            >
              <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-emerald-600 dark:bg-emerald-500 flex items-center justify-center text-white text-xl font-bold shadow-lg">
                {step.id}
              </div>
              <div className="absolute -top-4 -right-4 px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs font-semibold">
                {step.time}
              </div>
              <div className="text-6xl mb-6 mt-4">{step.icon}</div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">{step.title}</h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{step.description}</p>
              {i < solutionSteps.length - 1 && (
                <div className="hidden md:block absolute -right-8 top-1/2 -translate-y-1/2">
                  <ArrowRight className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// ─── FEATURES SECTION ─────────────────────────────────────────────────────────
const FeaturesSection = () => (
  <section id="features" className="py-20 md:py-28 bg-white dark:bg-gray-900">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
          Powerful Features that drive{' '}
          <span className="bg-gradient-to-r from-emerald-600 to-emerald-500 bg-clip-text text-transparent">
            Results
          </span>
        </h2>
        <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
          Everything you need to automate your WhatsApp business operations
        </p>
      </div>

      <div className="space-y-24">
        {featuresData.map((feat, idx) => (
          <div key={feat.id} className="grid md:grid-cols-2 gap-12 items-center">
            <div className={`space-y-6 ${idx % 2 === 1 ? 'md:order-2' : ''}`}>
              <div className="inline-block px-4 py-2 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-sm font-medium">
                Feature #{feat.id}
              </div>
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
                {feat.title}
              </h3>
              <p className="text-lg text-emerald-600 dark:text-emerald-400 font-semibold">
                {feat.subtitle}
              </p>
              <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
                {feat.description}
              </p>
              <ul className="space-y-3">
                {feat.features.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 dark:text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className={`relative ${idx % 2 === 1 ? 'md:order-1' : ''}`}>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={`${feat.image}?w=700&h=500&fit=crop`}
                  alt={feat.imageAlt}
                  className="w-full h-auto object-cover"
                />
                <div
                  className={`absolute inset-0 ${
                    idx % 2 === 0
                      ? 'bg-gradient-to-br from-emerald-600/10'
                      : 'bg-gradient-to-bl from-blue-600/10'
                  } to-transparent`}
                />
              </div>
              <div
                className={`absolute -z-10 w-72 h-72 ${
                  idx % 2 === 0
                    ? '-bottom-12 -right-12 bg-emerald-200/30 dark:bg-emerald-500/10'
                    : '-top-12 -left-12 bg-blue-200/30 dark:bg-blue-500/10'
                } rounded-full blur-3xl`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── ADDITIONAL FEATURES ──────────────────────────────────────────────────────
const AdditionalFeatures = () => (
  <section className="py-20 bg-gray-50 dark:bg-gray-800/50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
          Everything You Need to Succeed
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
          Complete tools to manage your WhatsApp business operations
        </p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {additionalFeatures.map((feat) => {
          const Icon = iconMap[feat.icon];
          return (
            <div
              key={feat.id}
              className="group bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 hover:border-emerald-500 dark:hover:border-emerald-500 hover:shadow-xl transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-4 group-hover:bg-emerald-600 transition-colors duration-300">
                <Icon className="w-6 h-6 text-emerald-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{feat.title}</h3>
              <p className="text-gray-600 dark:text-gray-300">{feat.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// ─── DEMO SECTION ─────────────────────────────────────────────────────────────
const DemoSection = () => (
  <section className="py-20 bg-white dark:bg-gray-900">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
          See <span className="text-emerald-600 dark:text-emerald-400">TrueChattr</span> in Action
        </h2>
        <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
          Watch how businesses are automating their WhatsApp with keyword replies and bulk messaging
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8 mb-12">
        {[
          {
            src: 'https://images.unsplash.com/photo-1608222351212-18fe0ec7b13b?w=800&h=500&fit=crop',
            alt: 'TrueChattr Analytics Dashboard',
            badge: '📊 Analytics Dashboard',
            btnLabel: 'View Dashboard',
          },
          {
            src: 'https://images.unsplash.com/photo-1605108222700-0d605d9ebafe?w=800&h=500&fit=crop',
            alt: 'TrueChattr Conversations',
            badge: '💬 Conversations',
            btnLabel: 'View Conversations',
          },
        ].map((item) => (
          <div
            key={item.badge}
            className="relative group rounded-2xl overflow-hidden shadow-2xl border-2 border-gray-200 dark:border-gray-700 hover:border-emerald-500 dark:hover:border-emerald-400 transition-all duration-300"
          >
            <img src={item.src} alt={item.alt} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold bg-emerald-600 hover:bg-emerald-700 text-white transition-all"
              >
                <Play className="w-5 h-5" /> {item.btnLabel}
              </Link>
            </div>
            <div className="absolute top-4 left-4 px-4 py-2 rounded-full bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm text-sm font-semibold text-gray-900 dark:text-white">
              {item.badge}
            </div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {[
          {
            src: 'https://images.unsplash.com/photo-1644035772775-8e26685ad2f0?w=400&h=300&fit=crop',
            title: 'WhatsApp Business API',
            desc: 'Official integration — fully compliant with WhatsApp policies',
          },
          {
            src: 'https://images.unsplash.com/photo-1618544976420-1f213fcf2052?w=400&h=300&fit=crop',
            title: 'Keyword Auto-Replies',
            desc: 'Set keywords, bot replies instantly — 24/7 automated responses',
          },
          {
            src: 'https://images.unsplash.com/photo-1734519654307-ceb306e4073a?w=400&h=300&fit=crop',
            title: 'Bulk Broadcasting',
            desc: 'Send offers to thousands of customers in one click',
          },
        ].map((card) => (
          <div
            key={card.title}
            className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700"
          >
            <img
              src={card.src}
              alt={card.title}
              className="w-full h-48 object-cover rounded-lg mb-4"
            />
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{card.title}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">{card.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── TESTIMONIALS ─────────────────────────────────────────────────────────────
const TestimonialsSection = () => (
  <section className="py-20 bg-gray-50 dark:bg-gray-800">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
          What Our{' '}
          <span className="text-emerald-600 dark:text-emerald-400">Early Users Say</span>
        </h2>
        <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
          Real feedback from our beta and early access users
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {testimonialsData.map((t) => (
          <div
            key={t.id}
            className="group bg-white dark:bg-gray-900 rounded-2xl p-8 border-2 border-gray-200 dark:border-gray-700 hover:border-emerald-500 dark:hover:border-emerald-400 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 relative"
          >
            <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-emerald-600 dark:bg-emerald-500 flex items-center justify-center shadow-lg">
              <Quote className="w-6 h-6 text-white" />
            </div>
            <div className="flex gap-1 mb-4">
              {[...Array(t.rating)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <p className="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed italic">
              "{t.content}"
            </p>
            <div className="mb-6 inline-block px-4 py-2 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-sm font-semibold">
              {t.metric}
            </div>
            <div className="flex items-center gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <img
                src={t.avatar}
                alt={t.name}
                className="w-12 h-12 rounded-full object-cover border-2 border-emerald-500"
              />
              <div>
                <h4 className="font-bold text-gray-900 dark:text-white">{t.name}</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t.role}</p>
                <p className="text-xs text-emerald-600 dark:text-emerald-400">{t.company}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 text-center">
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Join businesses already automating with TrueChattr
        </p>
        <div className="flex justify-center items-center gap-3">
          <div className="flex -space-x-2">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <img
                key={i}
                src={`https://i.pravatar.cc/40?img=${i}`}
                alt=""
                className="w-10 h-10 rounded-full border-2 border-white dark:border-gray-900"
              />
            ))}
          </div>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Early Access — Limited Spots Available
          </span>
        </div>
      </div>
    </div>
  </section>
);

// ─── PRICING SECTION ──────────────────────────────────────────────────────────

const PricingSection = () => {
  const [yearly, setYearly] = useState(false);

  return (
    <section
      id="pricing"
      className="py-20 bg-gradient-to-b from-white to-emerald-50 dark:from-gray-900 dark:to-gray-800"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Simple,{' '}
            <span className="text-emerald-600 dark:text-emerald-400">Transparent Pricing</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto mb-4">
            Choose your message volume — every plan includes all features
          </p>

          {/* "All features included" note */}
          <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 text-sm font-medium mb-6">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
            Auto-reply bot, analytics, lead capture, bulk broadcast — included in every plan, even free
          </div>

          <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-emerald-600 to-emerald-500 text-white shadow-lg mb-8">
            <Sparkles className="w-5 h-5" />
            <span className="font-semibold">
              🎁 Launch Offer: 50% OFF first 3 months for first 100 users!
            </span>
          </div>

          {/* Billing toggle */}
          <div className="flex items-center justify-center gap-4">
            <span className={`text-sm font-semibold ${!yearly ? 'text-gray-900 dark:text-white' : 'text-gray-400'}`}>
              Monthly
            </span>
            <button
              onClick={() => setYearly((y) => !y)}
              className={`relative w-12 h-6 rounded-full transition-colors duration-200 focus:outline-none ${
                yearly ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'
              }`}
              aria-label="Toggle yearly billing"
            >
              <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${yearly ? 'translate-x-6' : 'translate-x-0'}`} />
            </button>
            <span className={`text-sm font-semibold ${yearly ? 'text-gray-900 dark:text-white' : 'text-gray-400'}`}>
              Yearly
              <span className="ml-2 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">
                Save 40%
              </span>
            </span>
          </div>
        </div>

        {/* Plan cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-12">
          {pricingData.map((plan) => {
            const showPrice = plan.monthlyPrice !== null;

            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl p-8 border-2 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 flex flex-col ${
                  plan.popular
                    ? 'bg-gradient-to-br from-emerald-600 to-emerald-500 border-emerald-600 text-white scale-105'
                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-emerald-500 dark:hover:border-emerald-400'
                }`}
              >
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-yellow-400 text-yellow-900 text-sm font-bold whitespace-nowrap">
                    {plan.badge}
                  </div>
                )}

                <h3 className={`text-2xl font-bold mb-2 ${plan.popular ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                  {plan.name}
                </h3>
                <p className={`text-sm mb-6 ${plan.popular ? 'text-emerald-50' : 'text-gray-600 dark:text-gray-400'}`}>
                  {plan.description}
                </p>

                {/* Price */}
                <div className="mb-6">
                  {!showPrice ? (
                    <div className="flex items-baseline gap-1">
                      <span className={`text-5xl font-bold ${plan.popular ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                        {plan.price}
                      </span>
                      <span className={`text-lg ${plan.popular ? 'text-emerald-100' : 'text-gray-600 dark:text-gray-400'}`}>
                        {plan.period}
                      </span>
                    </div>
                  ) : (
                    <>
                      <span className={`text-sm line-through ${plan.popular ? 'text-emerald-200' : 'text-gray-400'}`}>
                        ${plan.monthlyPrice}/mo
                      </span>
                      <div className="flex items-baseline gap-1">
                        <span className={`text-5xl font-bold ${plan.popular ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                          ${yearly ? plan.yearlyPrice : Math.round(plan.monthlyPrice * 0.5)}
                        </span>
                        <span className={`text-lg ${plan.popular ? 'text-emerald-100' : 'text-gray-600 dark:text-gray-400'}`}>
                          /mo
                        </span>
                      </div>
                      {yearly ? (
                        <p className={`text-xs mt-1 font-semibold ${plan.popular ? 'text-yellow-300' : 'text-emerald-600'}`}>
                          Billed ${plan.yearlyPrice * 12}/year — Save ${(plan.monthlyPrice - plan.yearlyPrice) * 12}!
                        </p>
                      ) : (
                        <p className={`text-xs mt-1 font-semibold ${plan.popular ? 'text-yellow-300' : 'text-orange-500'}`}>
                          🎁 Launch offer: First 3 months only
                        </p>
                      )}
                    </>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className={`w-5 h-5 flex-shrink-0 mt-0.5 ${plan.popular ? 'text-white' : 'text-emerald-600 dark:text-emerald-400'}`} />
                      <span className={`text-sm ${plan.popular ? 'text-emerald-50' : 'text-gray-700 dark:text-gray-300'}`}>
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/register"
                  className={`w-full py-2.5 text-sm inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-all duration-200 ${
                    plan.popular
                      ? 'bg-white text-emerald-600 hover:bg-gray-100'
                      : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg'
                  }`}
                >
                  {plan.cta}
                </Link>
              </div>
            );
          })}
        </div>

        {/* Lifetime Deal */}
        <div className="relative max-w-4xl mx-auto">
          <div className="relative bg-gradient-to-br from-yellow-400 via-orange-400 to-red-500 rounded-2xl p-8 md:p-12 overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl" />
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm text-white font-semibold mb-4">
                  <TrendingUp className="w-5 h-5" /> Limited to 50 spots only
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-3">
                  {lifetimeDeal.title} 🔥
                </h3>
                <p className="text-xl text-white/90 mb-6">{lifetimeDeal.subtitle}</p>
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="text-5xl font-bold text-white">{lifetimeDeal.price}</span>
                  <span className="text-2xl line-through text-white/60">{lifetimeDeal.originalPrice}</span>
                </div>
                <p className="text-white/90 font-semibold mb-6">
                  {lifetimeDeal.savings} • One-time payment
                </p>
                <Link
                  to="/register"
                  className="px-6 py-3 inline-flex items-center justify-center gap-2 rounded-xl font-bold bg-white text-orange-600 hover:bg-gray-100 shadow-xl transition-all"
                >
                  Grab Lifetime Deal Now
                </Link>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <p className="text-white font-semibold mb-4">10,000 messages/month + all features, forever</p>
                <ul className="space-y-3">
                  {[
                    '10,000 messages/month — forever',
                    'Keyword auto-reply bot (unlimited keywords)',
                    'Analytics, lead capture, bulk broadcast',
                    'Lifetime access — no monthly fees ever',
                    'All future updates included',
                    'Priority onboarding support',
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
                      <span className="text-white/90 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── FAQ SECTION ──────────────────────────────────────────────────────────────
const FAQSection = () => {
  const [open, setOpen] = useState(null);

  return (
    <section id="faq" className="py-20 md:py-28 bg-white dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300">
            Everything you need to know about TrueChattr
          </p>
        </div>
        <div className="space-y-4">
          {faqData.map((faq) => (
            <div
              key={faq.id}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <button
                onClick={() => setOpen(open === faq.id ? null : faq.id)}
                className="w-full text-left px-6 py-5 flex items-center justify-between font-semibold text-gray-900 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
              >
                {faq.question}
                <span
                  className={`ml-4 flex-shrink-0 transition-transform duration-200 ${
                    open === faq.id ? 'rotate-45' : ''
                  }`}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                </span>
              </button>
              {open === faq.id && (
                <div className="px-6 pb-5 text-gray-600 dark:text-gray-300 leading-relaxed">
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="mt-12 text-center p-8 bg-gray-50 dark:bg-gray-800/50 rounded-2xl">
          <p className="text-lg text-gray-700 dark:text-gray-300 mb-4">Still have questions?</p>
          <p className="text-gray-600 dark:text-gray-400">
            Our team is here to help.{' '}
            <a
              href="#contact"
              className="text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 font-semibold underline"
            >
              Get in touch
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

// ─── FEEDBACK / CONTACT SECTION ───────────────────────────────────────────────
const FeedbackSection = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState('idle');

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      const res = await fetch(`${API_BASE}/api/waitlist/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setStatus('success');
      setForm({ name: '', email: '', message: '' });
      setTimeout(() => setStatus('idle'), 4000);
    } catch (err) {
      setStatus('idle');
      alert(err.message || 'Something went wrong! Try again.');
    }
  };

  return (
    <section id="contact" className="py-20 bg-gray-50 dark:bg-gray-800/50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 md:p-12 border border-gray-200 dark:border-gray-700">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 mb-4">
              <MessageCircle className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Share Your Feedback
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Have suggestions or questions? We'd love to hear from you!
            </p>
          </div>

          {status === 'success' && (
            <div className="mb-6 p-4 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-700 rounded-xl text-emerald-700 dark:text-emerald-300 text-center font-medium">
              🎉 Thank you for your feedback! We'll get back to you soon.
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Field
                id="feedback-name"
                label="Name *"
                name="name"
                type="text"
                placeholder="Your name"
                value={form.name}
                onChange={onChange}
                required
              />
              <Field
                id="feedback-email"
                label="Email *"
                name="email"
                type="email"
                placeholder="your@email.com"
                value={form.email}
                onChange={onChange}
                required
              />
            </div>
            <div className="space-y-2">
              <label
                htmlFor="feedback-msg"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Message *
              </label>
              <textarea
                id="feedback-msg"
                name="message"
                rows={6}
                placeholder="Share your thoughts, suggestions, or questions..."
                value={form.message}
                onChange={onChange}
                required
                className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all resize-none"
              />
            </div>
            <Btn type="submit" disabled={status === 'loading'} className="w-full py-4 text-lg">
              {status === 'loading' ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" /> Sending...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" /> Send Feedback
                </>
              )}
            </Btn>
          </form>
        </div>
      </div>
    </section>
  );
};

// ─── FINAL CTA SECTION ────────────────────────────────────────────────────────
const FinalCTASection = () => {
  const [form, setForm] = useState({ name: '', email: '', company: '', useCase: '' });
  const [status, setStatus] = useState('idle');

  const benefits = [
    'Setup completed in 48 hours',
    'Free training & onboarding',
    'No credit card required',
    'Cancel anytime',
  ];

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      const res = await fetch(`${API_BASE}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.company,
          businessType: form.useCase,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setStatus('success');
      setForm({ name: '', email: '', company: '', useCase: '' });
      setTimeout(() => setStatus('idle'), 5000);
    } catch (err) {
      setStatus('idle');
      alert(err.message || 'Something went wrong! Try again.');
    }
  };

  return (
    <section className="relative py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-emerald-500 to-emerald-700" />
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-400/20 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl p-8 md:p-12 border-4 border-emerald-300 dark:border-emerald-700">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <Rocket className="w-10 h-10 text-emerald-600 dark:text-emerald-400" />
            </div>
          </div>

          <div className="text-center mb-8">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Ready to Automate{' '}
              <span className="text-emerald-600 dark:text-emerald-400">Your WhatsApp?</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-4">
              Join businesses already on the early access waitlist
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-6">
              {benefits.map((b, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300"
                >
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">{b}</span>
                </div>
              ))}
            </div>
          </div>

          {status === 'success' && (
            <div className="mb-6 p-4 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-700 rounded-xl text-emerald-700 dark:text-emerald-300 text-center font-medium">
              🎉 Welcome to the waitlist! We'll contact you within 48 hours for onboarding.
            </div>
          )}

          <form onSubmit={onSubmit} className="max-w-2xl mx-auto space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Field
                label="Full Name *"
                name="name"
                type="text"
                placeholder="Your Name"
                value={form.name}
                onChange={onChange}
                required
              />
              <Field
                label="Email Address *"
                name="email"
                type="email"
                placeholder="your@email.com"
                value={form.email}
                onChange={onChange}
                required
              />
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <Field
                label="Company Name (Optional)"
                name="company"
                type="text"
                placeholder="Your Company"
                value={form.company}
                onChange={onChange}
              />
              <SelectField
                label="Business Type *"
                name="useCase"
                options={businessTypes}
                value={form.useCase}
                onChange={onChange}
                required
              />
            </div>
            <Btn
              type="submit"
              disabled={status === 'loading'}
              className="w-full py-4 text-lg"
            >
              {status === 'loading' ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin" /> Getting Your Spot...
                </>
              ) : (
                <>
                  <Rocket className="w-6 h-6" /> Get Early Access — It's Free
                </>
              )}
            </Btn>
            <p className="text-center text-sm text-gray-500 dark:text-gray-400">
              🔒 No credit card required • 100 messages free • Cancel anytime
            </p>
          </form>

          <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col md:flex-row items-center justify-center gap-6">
              <div className="flex -space-x-3">
                {[...Array(8)].map((_, i) => (
                  <img
                    key={i}
                    src={`https://i.pravatar.cc/50?img=${i + 11}`}
                    alt=""
                    className="w-12 h-12 rounded-full border-2 border-white dark:border-gray-900"
                  />
                ))}
              </div>
              <div className="text-center md:text-left">
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  Early Access — Limited Spots Available
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Average setup time: 5 minutes ⚡
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── FOOTER ───────────────────────────────────────────────────────────────────
const Footer = () => {
  const year = new Date().getFullYear();
  const cols = {
    Product: [
      { label: 'Features',     href: '#features'    },
      { label: 'Pricing',      href: '#pricing'      },
      { label: 'How It Works', href: '#how-it-works' },
      { label: 'FAQ',          href: '#faq'          },
    ],
    Company: [
      { label: 'About Us', href: '#about'   },
      { label: 'Contact',  href: '#contact' },
      { label: 'Blog',     href: '#blog'    },
      { label: 'Careers',  href: '#careers' },
    ],
    Legal: [
      { label: 'Privacy Policy',   href: '#privacy' },
      { label: 'Terms of Service', href: '#terms'   },
      { label: 'Cookie Policy',    href: '#cookies' },
    ],
  };
  const socials = [
    { Icon: Facebook,  href: '#', label: 'Facebook'  },
    { Icon: Twitter,   href: '#', label: 'Twitter'   },
    { Icon: Linkedin,  href: '#', label: 'LinkedIn'  },
    { Icon: Instagram, href: '#', label: 'Instagram' },
  ];

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          <div className="lg:col-span-2">
            <Logo className="mb-4" />
            <p className="text-gray-400 mb-6 max-w-sm">
              Automate your WhatsApp completely — keyword replies, bulk broadcasting, lead
              management, all in one dashboard. No coding required.
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <Mail className="w-4 h-4 text-emerald-500" />
                <a
                  href="mailto:hello@truechattr.in"
                  className="hover:text-emerald-400 transition-colors"
                >
                  hello@truechattr.in
                </a>
              </div>
            
              <div className="flex items-center gap-3 text-sm">
                <MapPin className="w-4 h-4 text-emerald-500" />
                <span>India</span>
              </div>
            </div>
          </div>

          {Object.entries(cols).map(([title, links]) => (
            <div key={title}>
              <h3 className="text-white font-semibold mb-4">{title}</h3>
              <ul className="space-y-3">
                {links.map((l) => (
                  <li key={l.label}>
                    <a
                      href={l.href}
                      className="text-sm hover:text-emerald-400 transition-colors"
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-gray-400">© {year} TRUECHATTR. All rights reserved.</p>
          <div className="flex items-center gap-4">
            {socials.map(({ Icon, href, label }) => (
              <a
                key={label}
                href={href}
                aria-label={label}
                className="w-10 h-10 rounded-full bg-gray-800 hover:bg-emerald-600 flex items-center justify-center transition-colors"
              >
                <Icon className="w-5 h-5" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

// ─── ROOT EXPORT ──────────────────────────────────────────────────────────────
export default function LandingPage() {
  return (
    <LandingThemeProvider>
      <Helmet>
        <title>
          TrueChattr - WhatsApp Automation Platform | Bulk Messaging, Auto-Reply & Lead Management
        </title>
        <meta
          name="description"
          content="TrueChattr — Automate your WhatsApp completely. Send bulk messages to 1000+ customers, set keyword auto-replies, manage leads — all in one dashboard. No coding required."
        />
        <meta
          name="keywords"
          content="WhatsApp automation, WhatsApp bulk message, WhatsApp auto reply, WhatsApp business automation, WhatsApp keyword bot, lead management WhatsApp, TrueChattr"
        />
        <meta name="robots" content="index, follow" />

        <meta property="og:title" content="TrueChattr - WhatsApp Automation Platform" />
        <meta
          property="og:description"
          content="Send bulk messages, keyword auto-replies, track leads — all in one WhatsApp dashboard. No coding required."
        />
        <meta property="og:url" content="https://truechattr.in" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="TrueChattr" />
        <meta property="og:image" content="https://truechattr.in/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="TrueChattr - WhatsApp Automation Platform" />
        <meta
          name="twitter:description"
          content="Send bulk messages, keyword auto-replies, track leads — all in one WhatsApp dashboard."
        />
        <meta name="twitter:image" content="https://truechattr.in/og-image.png" />

        <link rel="canonical" href="https://truechattr.in/" />

        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "TrueChattr",
            "url": "https://truechattr.in",
            "description": "WhatsApp automation platform — bulk messaging, keyword auto-reply, lead management, all in one dashboard. No coding required.",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "creator": {
              "@type": "Organization",
              "name": "TrueChattr",
              "url": "https://truechattr.in"
            }
          }
        `}</script>
      </Helmet>

      <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
        <Header />
        <main>
          <HeroSection />
          <StatsSection />
          <ProblemSection />
          <SolutionSection />
          <FeaturesSection />
          <AdditionalFeatures />
          <DemoSection />
          <TestimonialsSection />
          <PricingSection />
          <FAQSection />
          <FeedbackSection />
          <FinalCTASection />
        </main>
        <Footer />
      </div>
    </LandingThemeProvider>
  );
}
